import { useEffect } from 'react'

import { useQueryClient } from '@tanstack/react-query'

import { supabase } from '@/lib/supabase/cliente'

/**
 * LA MATRIZ SE ACTUALIZA SOLA.
 *
 * Johan: «esa matriz de órdenes debería actualizarse sola sin yo tener que
 * refrescar cada rato». Una orden que levantaba un técnico, una cotización
 * que aprobaba gerencia o una llegada marcada en la obra no aparecían hasta
 * recargar la página.
 *
 * Se escuchan en vivo las cuatro tablas que mueven la matriz y la ficha
 * (0227) y, cuando cambia algo, se refresca lo que cuelga de
 * 'ordenes-servicio' —la lista, la ficha abierta, sus documentos y su total—.
 *
 * AGRUPADO: una aprobación toca la cotización, la orden y crea la orden de
 * trabajo en el mismo instante. Sin agrupar serían tres recargas seguidas;
 * se espera medio segundo y se hace una.
 *
 * Y por si la conexión en vivo se cae sin avisar —pasa con el wifi de la
 * oficina—, la lista además se repasa sola cada minuto (useOrdenesServicio).
 */
export function useOrdenesEnVivo() {
  const qc = useQueryClient()

  useEffect(() => {
    let pendiente: ReturnType<typeof setTimeout> | null = null

    const refrescar = () => {
      if (pendiente) return
      pendiente = setTimeout(() => {
        pendiente = null
        void qc.invalidateQueries({ queryKey: ['ordenes-servicio'] })
        // El tablero y la programación leen las mismas órdenes por otro lado.
        void qc.invalidateQueries({ queryKey: ['tablero'] })
        void qc.invalidateQueries({ queryKey: ['pendientes-de-decision'] })
        void qc.invalidateQueries({ queryKey: ['visitas'] })
        // Y el reloj de cada tarjeta: una orden que cambia de estación
        // empieza a contar de cero.
        void qc.invalidateQueries({ queryKey: ['recorridos'] })
        // Y el tablero entero: cifras, decisiones, plata y equipo. Johan: «el
        // tablero también debería actualizarse solo, sin refrescar».
        for (const clave of [
          'lo-que-espera',
          'indicadores-cotizacion',
          'ordenes-esperando',
          'plata-en-juego',
          'cuello-de-botella',
          'trazabilidad',
          'cronograma',
        ]) {
          void qc.invalidateQueries({ queryKey: [clave] })
        }
        void qc.invalidateQueries({ queryKey: ['recorrido'] })
      }, 500)
    }

    let canal = supabase.channel('ordenes-en-vivo')
    for (const tabla of ['ordenes_servicio', 'cotizaciones', 'ordenes_trabajo', 'visitas']) {
      canal = canal.on('postgres_changes', { event: '*', schema: 'public', table: tabla }, refrescar)
    }
    canal.subscribe()

    return () => {
      if (pendiente) clearTimeout(pendiente)
      void supabase.removeChannel(canal)
    }
  }, [qc])
}
